import React, { useState, useRef } from 'react';
import { SoliloquyEditor } from './components/editor/SoliloquyEditor';
import type { Editor } from '@tiptap/core';
import { NavigatorPanel } from './components/panels/NavigatorPanel';

export default function SoliloquyApp() {
  const [title, setTitle] = useState('Untitled Play');
  const [showNavigator, setShowNavigator] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('soliloquy-theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const editorRef = useRef<Editor | null>(null);

  // Apply dark mode class to html element
  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('soliloquy-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <div className="h-screen flex flex-col bg-ink-100 dark:bg-ink-950" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="bg-white dark:bg-ink-900 border-b border-ink-200 dark:border-ink-700 px-5 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <span className="font-brand text-2xl text-ink-900 dark:text-ink-100 select-none tracking-tight">soliloquy</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-base text-ink-700 dark:text-ink-300 bg-transparent outline-none border-b border-transparent hover:border-ink-300 dark:hover:border-ink-600 focus:border-ink-500 dark:focus:border-ink-400 transition-colors ml-2 py-0.5"
            style={{ fontFamily: "'Times New Roman', serif" }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNavigator(!showNavigator)}
            className={`text-[11px] px-2 py-1 rounded transition-colors ${
              showNavigator
                ? 'bg-ink-200 dark:bg-ink-700 text-ink-800 dark:text-ink-200'
                : 'text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800'
            }`}
            title="Toggle navigator"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M2 4h12M2 8h8M2 12h10" />
            </svg>
          </button>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="text-[11px] text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300 px-2 py-1 rounded hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
            title="Toggle dark mode"
          >
            {darkMode ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="3" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M13.5 9.5A5.5 5.5 0 016.5 2.5a6 6 0 107 7z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Main area with optional navigator sidebar */}
      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-ink-100 dark:bg-ink-950 py-10 px-6 pb-16">
          <div className="max-w-3xl mx-auto bg-white dark:bg-ink-900 shadow-sm dark:shadow-none dark:ring-1 dark:ring-ink-800 min-h-full rounded-sm">
            <div className="py-16 px-12">
              <SoliloquyEditor
                globalFont="Times New Roman"
                title={title}
                onTitleChange={setTitle}
                onEditorReady={(e) => { editorRef.current = e; }}
              />
            </div>
          </div>
        </main>

        {/* Navigator sidebar */}
        {showNavigator && editorRef.current && (
          <NavigatorPanel
            editor={editorRef.current}
            onClose={() => setShowNavigator(false)}
          />
        )}
      </div>
    </div>
  );
}
